import { Link } from 'react-router';
import { Tag, Dot } from '@/components/Tag';
import { money, num } from '@/lib/format';
import { orderStatus } from '@/lib/status';
import type { OrderStatus } from '@/api/types';
import { has, signedMoney } from './api';
import { EPS, LABELS, cashState, invoiceState, statementStatus } from './status';
import type { Scope, StatementStatus, SummaryRow } from './types';
import type { Col } from './ui';
import s from './PartnerList.module.css';

/** 结算方式一行：月结 · 每月 25 日 · 账期 30 天（老系统 getPartnerMeta） */
export function termsText(p: { isMonthlySettlement?: boolean; settlementDay?: number | null; paymentTermDays?: number | null }) {
  const parts = [p.isMonthlySettlement ? '月结' : '非月结'];
  if (p.settlementDay) parts.push(`每月 ${p.settlementDay} 日`);
  if (p.paymentTermDays != null) parts.push(`账期 ${p.paymentTermDays} 天`);
  return parts.join(' · ');
}

/** 待收 / 待付：欠钱红色，超收 / 超付紫色负数，结清灰色 */
export function BalanceText({ v, className }: { v?: number; className?: string }) {
  if (v === undefined) return <span className="em">—</span>;
  const n = num(v);
  const cls = n > EPS ? s.owe : n < -EPS ? s.over : 'em';
  return <span className={`tnum ${cls} ${className || ''}`}>{signedMoney(n)}</span>;
}

/**
 * 往来单位卡：① 单位 + 资金状态（全卡唯一实底标签）② 结算方式 ③ 应结 / 已结 / 待结三格，位置固定、跨卡能对齐
 * ④ 提醒行（开票差额、单据与记录数）。没有金额字段时金额三格整块不渲染。
 */
export function PartnerCard({ row, scope, to, selected }: { row: SummaryRow; scope: Scope; to?: string; selected?: boolean }) {
  const L = LABELS[scope];
  const cs = cashState(row.balanceAmount, scope);
  const age = row.agingBucket && row.agingBucket !== '未到期' ? row.agingBucket : null;
  const inv = invoiceState(row.invoiceBalanceAmount, scope);
  const amounts = has(row.billAmount);
  const invBal = num(row.invoiceBalanceAmount);
  const statusLabel = row.cashStatus === 'open' ? '未结清' : row.cashStatus === 'over' ? L.over : row.cashStatus === 'settled' ? '已结清' : null;
  const invoiceLabel = row.invoiceStatus === 'open' ? L.invOpen : row.invoiceStatus === 'over' ? L.invOver : row.invoiceStatus === 'settled' ? L.invDone : null;
  const statementLabel = row.currentStatement && statementStatus[row.currentStatement.status as StatementStatus]?.label;
  const content = (
    <>
      <div className={s.h}>
        <b>{row.partnerName}</b>
        {row.partnerCode && <span className={s.code}>{row.partnerCode}</span>}
        {age && cs?.label === L.open ? <Tag tone={age === '0–30 天' || age === '31–60 天' ? 'amber' : 'red'} sm>{age}</Tag> : cs && <Tag tone={cs.tone} sm>{cs.label}</Tag>}
        {!amounts && statusLabel && <Tag tone={row.cashStatus === 'open' ? 'amber' : row.cashStatus === 'over' ? 'violet' : 'green'} sm>{statusLabel}</Tag>}
      </div>
      <div className={s.sub}>{termsText(row)}</div>
      {amounts ? (
        <div className={s.g}>
          <div><i>{scope === 'customer' ? '业务金额' : '采购金额'}</i><b className="tnum">{money(row.billAmount)}</b></div>
          <div><i>{L.cashShort}</i><b className="tnum">{money(row.cashAmount)}</b></div>
          <div><i>{scope === 'customer' ? '未收' : '未付'}</i><b><BalanceText v={row.balanceAmount} /></b></div>
        </div>
      ) : row.followedOrders ? (
        <>
          <div className={s.followerGrid}>
            <div><i>开票</i><b>{row.pendingInvoiceCount ? `待开票 ${row.pendingInvoiceCount} 张` : invoiceLabel || '—'}</b></div>
            <div><i>本月对账单</i><b>{statementLabel || '—'}</b></div>
            <div><i>我跟的订单</i><b>{row.followedOrderCount ?? 0} 张</b></div>
          </div>
          <div className={s.orderRefs}>{row.followedOrders.map((order) => <Link key={order.id} to={`/orders/${order.id}`}>{order.orderNo} · {orderStatus[order.status as OrderStatus]?.label || order.status}</Link>)}</div>
        </>
      ) : <div className={s.statusRow}><span>{invoiceLabel || '开票状态未知'}</span><span>相关订单 {row.orderCount} 张</span></div>}
      {!row.followedOrders && <div className={s.f}>
        {inv && (
          <Dot tone={inv.dot}>
            {inv.label}
            {Math.abs(invBal) > EPS && <span className="tnum">&nbsp;{money(Math.abs(invBal))}</span>}
          </Dot>
        )}
        <span className={s.cnt}>{row.orderCount} 张{L.docs} · {row.cashRecordCount} 笔{L.cashVerb} · {row.invoiceRecordCount} 笔{L.invVerb}</span>
      </div>}
    </>
  );
  const className = [s.card, selected && s.sel].filter(Boolean).join(' ');
  return to ? <Link to={to} className={className}>{content}</Link> : <div className={className}>{content}</div>;
}

/** T1/T2 桌面与 iPad 表格，账龄只显示逐单未结余额算出的最老档位。 */
export function partnerCols(scope: Scope, rows: SummaryRow[]): Col<SummaryRow>[] {
  const L = LABELS[scope];
  const amounts = rows.length === 0 || rows.some((r) => has(r.billAmount));
  const follower = rows.some((r) => r.followedOrders !== undefined);
  const m = (v?: number) => (v === undefined ? <span className="em">—</span> : <span className="tnum">{money(v)}</span>);
  const cols: (Col<SummaryRow> | false)[] = [
    {
      key: 'name', title: L.partner, w: 220, fix: true,
      render: (r) => (
        <div className={s.cell2}>
          <b>{r.partnerName}</b>
          <small>{r.partnerCode || '—'}</small>
        </div>
      ),
    },
    { key: 'terms', title: '结算方式', w: 150, render: (r) => <div className={s.cell2}><b>{r.isMonthlySettlement ? '月结' : '现结'}</b><small>{r.isMonthlySettlement ? `${r.paymentTermDays ?? '—'} 天 · ${r.settlementDay ? `每月 ${r.settlementDay} 日` : '月底'}` : '—'}</small></div> },
    amounts && { key: 'bill', title: scope === 'customer' ? '业务金额' : '采购金额', w: 132, align: 'r', render: (r) => m(r.billAmount) },
    amounts && { key: 'cash', title: L.cashShort, w: 132, align: 'r', render: (r) => m(r.cashAmount) },
    amounts && { key: 'open', title: scope === 'customer' ? '未收' : '未付', w: 132, align: 'r', render: (r) => <BalanceText v={r.balanceAmount} /> },
    amounts && { key: 'inv', title: L.inv, w: 124, align: 'r', render: (r) => m(r.invoiceAmount) },
    amounts && {
      key: 'invOpen', title: scope === 'customer' ? '未开票' : '未收票', w: 132, align: 'r',
      render: (r) => { const n = num(r.invoiceBalanceAmount); return <span className={`tnum ${n > EPS ? s.warn : n < -EPS ? s.blue : 'em'}`}>{signedMoney(n)}</span>; },
    },
    amounts && { key: 'age', title: '账龄', w: 112, render: (r) => r.agingBucket || <span className="em">—</span> },
    !amounts && { key: 'cashStatus', title: scope === 'customer' ? '收款状态' : '付款状态', w: 110, render: (r) => r.cashStatus === 'open' ? '未结清' : r.cashStatus === 'over' ? L.over : r.cashStatus === 'settled' ? '已结清' : '—' },
    !amounts && { key: 'invoiceStatus', title: '开票', w: 110, render: (r) => r.pendingInvoiceCount ? `待开票 ${r.pendingInvoiceCount} 张` : r.invoiceStatus === 'open' ? L.invOpen : r.invoiceStatus === 'over' ? L.invOver : r.invoiceStatus === 'settled' ? L.invDone : '—' },
    !amounts && follower && { key: 'statement', title: '本月对账单', w: 150, render: (r) => <div className={s.cell2}><b>{r.currentStatement ? statementStatus[r.currentStatement.status as StatementStatus]?.label || r.currentStatement.status : '—'}</b><small>{r.currentStatement?.statementNo}</small></div> },
    !amounts && { key: 'relatedOrders', title: follower ? '我跟的订单' : '相关订单', w: follower ? 230 : 100, render: (r) => follower ? <div className={s.cell2}><b>{r.followedOrderCount ?? 0} 张</b><small>{r.followedOrders?.length ? r.followedOrders.map((order) => <Link key={order.id} to={`/orders/${order.id}`} className={s.orderCellLink}>{order.orderNo} · {orderStatus[order.status as OrderStatus]?.label || order.status}</Link>) : '—'}</small></div> : `${r.orderCount} 张` },
    { key: 'action', title: '操作', w: 74, fixRight: true, render: () => <span className={s.action}>查看 ›</span> },
  ];
  return cols.filter(Boolean) as Col<SummaryRow>[];
}
